import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from "react-oidc-context";
import AppLayout from './AppLayout';
import ProtectedRoute from './context/ProtectedRoute';
import { PageLoading } from './components/ui';
import { useTenant } from './context/TenantContext';
import { ListFilterProvider } from './context/ListFilterContext';
import { ConnectionStatusProvider } from './context/ConnectionStatusContext';
import PaywallPage from './pages/paywall/PaywallPage';
import CustomLoginPage from './pages/login/CustomLoginPage';
import { useLanguageSync } from './hooks/useLanguageSync';
import './App.css';

// Wrapper to provide list filter context only for funnel/recording pages
const WithListFilter = ({ children }) => (
  <ListFilterProvider>{children}</ListFilterProvider>
);

// Lazy load page components for better initial load performance
const RecordingPage = lazy(() => import('./pages/recording/RecordingPage'));
const FunnelPage = lazy(() => import('./pages/funnel/FunnelPage'));
const MyTasksPage = lazy(() => import('./pages/tasks/MyTasksPage'));
const ListsPage = lazy(() => import('./pages/lists/ListsPage'));
const AccountPage = lazy(() => import('./pages/account/AccountPage'));
const DncPage = lazy(() => import('./pages/account/DncPage'));
const QuotasPage = lazy(() => import('./pages/quotas/QuotasPage'));
const TeamPage = lazy(() => import('./pages/team/TeamPage'));
// Beta
const TemplatesPage = lazy(() => import('./pages/scripts/TemplatesPage'));
const EngagementsPage = lazy(() => import('./pages/engagements/EngagementsPage'));
const PostsPage = lazy(() => import('./pages/posts/PostsPage'));
const MessagesManagementPage = lazy(() => import('./pages/message-queue/MessagesManagementPage'));
const ContactsPage = lazy(() => import('./pages/contacts/ContactsPage'));
// Dev
const PetriPage = lazy(() => import('./pages/petri/PetriPage'));
const FunnelNetPage = lazy(() => import('./pages/petri/FunnelNetPage'));
const AgentPage = lazy(() => import('./pages/agent/AgentPage'));
const AgentControlPage = lazy(() => import('./pages/agent-control/AgentControlPage'));
const QMLPage = lazy(() => import('./pages/qml/QMLPage'));
const ChatPage = lazy(() => import('./pages/chat/ChatPage'));
const VideoArchivePage = lazy(() => import('./pages/video-archive/VideoArchivePage'));
const VerificationPage = lazy(() => import('./pages/verification/VerificationPage'));
// Enterprise
const APIAccessPage = lazy(() => import('./pages/api-access/APIAccessPage'));
const APIDocsPage = lazy(() => import('./pages/api-docs/APIDocsPage'));
// Admin
const AdminPage = lazy(() => import('./pages/admin/AdminPage'));
const SimulatePage = lazy(() => import('./pages/admin/SimulatePage'));
// Internal Tools
const AnalyticsPage = lazy(() => import('./pages/analytics/AnalyticsPage'));
const FAQPage = lazy(() => import('./pages/faq/FAQPage'));
const UIShowcasePage = lazy(() => import('./pages/ui-showcase/UIShowcasePage'));
// UI MOCK (plan 96) — dev-only clickable mock, SBD-admin gated. Delete after /implement.
const PostEngagersMockPage = lazy(() => import('./pages/ui-showcase/post-engagers-mock/PostEngagersMockPage'));
// UI MOCK (plan 97) — dev-only clickable mock, two-user allowlist gated. Delete after /implement.
const CommentInstructionsMockPage = lazy(() => import('./pages/ui-showcase/comment-instructions-mock/CommentInstructionsMockPage'));
// UI MOCK (plan 98) — dev-only clickable mock, two-user allowlist gated. Delete after /implement.
// UI MOCK (plan 97 — unseen inbox) — dev-only clickable mock, two-user allowlist gated. Delete after /implement.
const UnseenInboxMockPage = lazy(() => import('./pages/ui-showcase/unseen-inbox-mock/UnseenInboxMockPage'));
// UI MOCK (plan 104 — recording queue status integrity) — dev-only clickable mock, two-user allowlist gated. Delete after /implement.
const RecordingQueueMockPage = lazy(() => import('./pages/ui-showcase/recording-queue-mock/RecordingQueueMockPage'));
// UI MOCK (plan 102 — LinkedIn post scheduler / "Content" page) — dev-only clickable mock, two-user allowlist gated. Delete after /implement.
const PostsMockPage = lazy(() => import('./pages/ui-showcase/posts-mock/PostsMockPage'));
// UI MOCK (plan 103 — human verification + video certificates) — dev-only clickable mock, two-user allowlist gated. Delete after /implement.
const HumanVerificationMockPage = lazy(() => import('./pages/ui-showcase/human-verification-mock/HumanVerificationMockPage'));
// UI MOCK (plan 112 — "Human Verified" video end-slate) — dev-only static visual mock, two-user allowlist gated. Delete after /implement.
const VideoEndSlateMock = lazy(() => import('./pages/ui-showcase/human-verification-mock/VideoEndSlateMock'));
// UI MOCK (plan 108 — expired connection state) — dev-only clickable mock, two-user allowlist gated. Delete after /implement.
const ExpiredStateMockPage = lazy(() => import('./pages/ui-showcase/expired-state-mock/ExpiredStateMockPage'));
// UI MOCK (plan 109 — analytics multiselect list filter) — dev-only clickable mock, two-user allowlist gated. Delete after /implement.
const AnalyticsListFilterMockPage = lazy(() => import('./pages/ui-showcase/analytics-list-filter-mock/AnalyticsListFilterMockPage'));
// UI MOCK (plan 113 — per-list weighted send split) — dev-only clickable mock, two-user allowlist gated. Delete after /implement.
const WeightedSendSplitMockPage = lazy(() => import('./pages/ui-showcase/weighted-send-split-mock/WeightedSendSplitMockPage'));
// UI MOCK (funnel display reframe — Connected vs In Sequence) — dev-only clickable mock, two-user allowlist gated. Delete after /implement.
const FunnelDisplayMockPage = lazy(() => import('./pages/ui-showcase/funnel-display-mock/FunnelDisplayMockPage'));

const ExcludeSequenceStateMockPage = lazy(() => import('./pages/ui-showcase/exclude-sequence-state-mock/ExcludeSequenceStateMockPage'));
// UI MOCK (plan 118 — sequence image step) — dev-only clickable mock, two-user allowlist gated. Delete after /implement.
const SequenceImageStepMockPage = lazy(() => import('./pages/ui-showcase/sequence-image-step-mock/SequenceImageStepMockPage'));

const CompaniesPage = lazy(() => import('./pages/companies/CompaniesPage'));
const SignalsPage = lazy(() => import('./pages/signals/SignalsPage'));
const CoordinationPage = lazy(() => import('./pages/coordination/CoordinationPage'));
const GraphPage = lazy(() => import('./pages/graph/SenderHealthPage'));

const App = () => {
  const [activeList, setActiveList] = useState(null);
  const { hasTenant, isLoading } = useTenant();
  const auth = useAuth();
  useLanguageSync();

  useEffect(() => {
    document.body.classList.add('app-styles');

    return () => {
      document.body.classList.remove('app-styles');
    };
  }, []);

  // Show loading while checking auth or tenant
  if (auth.isLoading || isLoading) {
    return (
      <div className="video-app" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#0f172a'
      }}>
        <div style={{ color: '#fff', fontSize: '16px' }}>Loading...</div>
      </div>
    );
  }

  // Show paywall if user IS authenticated but has no tenant
  // If not authenticated, let ProtectedRoute handle the login redirect
  if (auth.isAuthenticated && !hasTenant) {
    return <PaywallPage auth={auth} />;
  }

  return (
    <ConnectionStatusProvider>
    <div className="video-app">
      <Toaster position="top-right" />
      <AppLayout>
        <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/login" element={<CustomLoginPage />} />
          <Route path="/" element={
            <ProtectedRoute>
              <WithListFilter>
                <FunnelPage demoMode={false} />
              </WithListFilter>
            </ProtectedRoute>
          } />
          <Route path="/recording" element={
            <ProtectedRoute>
              <WithListFilter>
                <RecordingPage activeList={activeList} demoMode={false} />
              </WithListFilter>
            </ProtectedRoute>
          } />
          <Route path="/funnel" element={
            <ProtectedRoute>
              <WithListFilter>
                <FunnelPage demoMode={false} />
              </WithListFilter>
            </ProtectedRoute>
          } />
          <Route path="/funnel/:listId" element={
            <ProtectedRoute>
              <WithListFilter>
                <FunnelPage demoMode={false} />
              </WithListFilter>
            </ProtectedRoute>
          } />
          <Route path="/tasks" element={
            <ProtectedRoute>
              <WithListFilter>
                <MyTasksPage />
              </WithListFilter>
            </ProtectedRoute>
          } />
          <Route path="/signals" element={
            <ProtectedRoute>
              <SignalsPage />
            </ProtectedRoute>
          } />
          <Route path="/coordination" element={
            <ProtectedRoute>
              <CoordinationPage />
            </ProtectedRoute>
          } />
          <Route path="/graph" element={
            <ProtectedRoute>
              <GraphPage />
            </ProtectedRoute>
          } />
          <Route path="/campaigns" element={
            <ProtectedRoute>
              <ListsPage setActiveList={setActiveList} demoMode={true} />
            </ProtectedRoute>
          } />
          <Route path="/lists" element={<Navigate to="/campaigns" replace />} />
          <Route path="/account" element={
            <ProtectedRoute>
              <AccountPage />
            </ProtectedRoute>
          } />
          <Route path="/account/dnc" element={
            <ProtectedRoute>
              <DncPage />
            </ProtectedRoute>
          } />
          <Route path="/api-access" element={
            <ProtectedRoute>
              <APIAccessPage />
            </ProtectedRoute>
          } />
          <Route path="/api-docs" element={
            <ProtectedRoute>
              <APIDocsPage />
            </ProtectedRoute>
          } />
          <Route path="/limits" element={
            <ProtectedRoute>
              <QuotasPage />
            </ProtectedRoute>
          } />
          <Route path="/team" element={
            <ProtectedRoute>
              <TeamPage />
            </ProtectedRoute>
          } />
          <Route path="/faq" element={
            <ProtectedRoute>
              <FAQPage />
            </ProtectedRoute>
          } />
          <Route path="/scripts" element={
            <ProtectedRoute>
              <TemplatesPage />
            </ProtectedRoute>
          } />
          <Route path="/posts" element={
            <ProtectedRoute>
              <PostsPage />
            </ProtectedRoute>
          } />
          <Route path="/engagements" element={
            <ProtectedRoute>
              <EngagementsPage />
            </ProtectedRoute>
          } />
          <Route path="/messages" element={
            <ProtectedRoute>
              <MessagesManagementPage />
            </ProtectedRoute>
          } />
          <Route path="/contacts" element={
            <ProtectedRoute>
              <ContactsPage />
            </ProtectedRoute>
          } />
          <Route path="/petri" element={
            <ProtectedRoute>
              <PetriPage />
            </ProtectedRoute>
          } />
          <Route path="/petri/funnel" element={
            <ProtectedRoute>
              <FunnelNetPage />
            </ProtectedRoute>
          } />
          <Route path="/agent-training" element={
            <ProtectedRoute>
              <AgentPage />
            </ProtectedRoute>
          } />
          <Route path="/agent" element={
            <ProtectedRoute>
              <AgentControlPage />
            </ProtectedRoute>
          } />
          <Route path="/qml" element={
            <ProtectedRoute>
              <QMLPage />
            </ProtectedRoute>
          } />
          <Route path="/chat" element={
            <ProtectedRoute>
              <ChatPage />
            </ProtectedRoute>
          } />
          <Route path="/video-archive" element={
            <ProtectedRoute>
              <VideoArchivePage />
            </ProtectedRoute>
          } />
          <Route path="/verification" element={
            <ProtectedRoute>
              <VerificationPage />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/simulate" element={
            <ProtectedRoute>
              <SimulatePage />
            </ProtectedRoute>
          } />
          <Route path="/ui" element={
            <ProtectedRoute>
              <UIShowcasePage />
            </ProtectedRoute>
          } />
          {/* UI MOCK (plan 96) — dev-only, remove after implementation */}
          <Route path="/ui/post-engagers" element={
            <ProtectedRoute>
              <PostEngagersMockPage />
            </ProtectedRoute>
          } />
          {/* UI MOCK (plan 97) — dev-only, remove after implementation */}
          <Route path="/ui/comment-instructions" element={
            <ProtectedRoute>
              <CommentInstructionsMockPage />
            </ProtectedRoute>
          } />
          {/* UI MOCK (plan 97 — unseen inbox) — dev-only, remove after implementation */}
          <Route path="/ui/unseen-inbox" element={
            <ProtectedRoute>
              <UnseenInboxMockPage />
            </ProtectedRoute>
          } />
          {/* UI MOCK (plan 104 — recording queue status integrity) — dev-only, remove after implementation */}
          <Route path="/ui/recording-queue" element={
            <ProtectedRoute>
              <RecordingQueueMockPage />
            </ProtectedRoute>
          } />
          {/* UI MOCK (plan 102 — LinkedIn post scheduler / "Content" page) — dev-only, remove after implementation */}
          <Route path="/ui/posts" element={
            <ProtectedRoute>
              <PostsMockPage />
            </ProtectedRoute>
          } />
          {/* UI MOCK (plan 103 — human verification + video certificates) — dev-only, remove after implementation */}
          <Route path="/ui/human-verification" element={
            <ProtectedRoute>
              <HumanVerificationMockPage />
            </ProtectedRoute>
          } />
          {/* UI MOCK (plan 112 — "Human Verified" video end-slate) — dev-only, remove after implementation */}
          <Route path="/ui/video-end-slate" element={
            <ProtectedRoute>
              <VideoEndSlateMock />
            </ProtectedRoute>
          } />
          {/* UI MOCK (plan 108 — expired connection state) — dev-only, remove after implementation */}
          <Route path="/ui/expired-state" element={
            <ProtectedRoute>
              <ExpiredStateMockPage />
            </ProtectedRoute>
          } />
          {/* UI MOCK (plan 109 — analytics multiselect list filter) — dev-only, remove after implementation */}
          <Route path="/ui/analytics-list-filter" element={
            <ProtectedRoute>
              <AnalyticsListFilterMockPage />
            </ProtectedRoute>
          } />
          {/* UI MOCK (plan 113 — per-list weighted send split) — dev-only, remove after implementation */}
          <Route path="/ui/weighted-send-split" element={
            <ProtectedRoute>
              <WeightedSendSplitMockPage />
            </ProtectedRoute>
          } />
          {/* UI MOCK — funnel display reframe (Connected vs In Sequence) — dev-only, remove after implementation */}
          <Route path="/ui/funnel-display" element={
            <ProtectedRoute>
              <FunnelDisplayMockPage />
            </ProtectedRoute>
          } />
          {/* UI MOCK (plan 117 — exclude as a per-list reversible `excluded` state) — dev-only, remove after implementation */}
          <Route path="/ui/exclude-sequence-state" element={
            <ProtectedRoute>
              <ExcludeSequenceStateMockPage />
            </ProtectedRoute>
          } />
          {/* UI MOCK (plan 118 — sequence image step) — dev-only, remove after implementation */}
          <Route path="/ui/sequence-image-step" element={
            <ProtectedRoute>
              <SequenceImageStepMockPage />
            </ProtectedRoute>
          } />
          <Route path="/analytics" element={
            <ProtectedRoute>
              <AnalyticsPage />
            </ProtectedRoute>
          } />

          <Route path="/companies" element={
            <ProtectedRoute>
              <CompaniesPage />
            </ProtectedRoute>
          } />
        </Routes>
        </Suspense>
      </AppLayout>
    </div>
    </ConnectionStatusProvider>
  );
};

export default App;